import { useState, useEffect, useRef } from 'react';
import {FaTimes} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useAuth } from '../../../Auth/AuthContext';
import styles from '../Preguntas/AnadirPregunta.module.css'; 

const EditarResultados = ({
    area,
    isOpen,
    onClose,
    onSuccess
}) => {
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        ar_codigo: '',
        ar_nombre: '',
        ar_descripcion: '',
        ar_orden: 0
    });
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const modalRef = useRef(null);

    useEffect(() => {
        if (isOpen && area) {
            setEditForm({
                ar_codigo: area.ar_codigo || '',
                ar_nombre: area.ar_nombre || '',
                ar_descripcion: area.ar_descripcion || '',
                ar_orden: area.ar_orden || 0
            });
        }
    }, [isOpen, area]);

    useEffect(() => {
        if (isOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isOpen]);

    // Manejar eventos de teclado
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isOpen) return;

            if (event.key === 'Escape' && !saving) {
                handleCancel();
            }

            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                handleSave();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, saving]);

    const handleInputChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        if (!editForm.ar_codigo?.trim() || !editForm.ar_nombre?.trim()) {
            Swal.fire({
                title: 'Campos requeridos',
                text: 'El código y nombre son obligatorios',
                icon: 'warning',
                confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                }
            });
            return;
        }

        try {
            setSaving(true);

            const updateData = {
                ar_codigo: editForm.ar_codigo.trim().toUpperCase(),
                ar_nombre: editForm.ar_nombre.trim(),
                ar_descripcion: editForm.ar_descripcion?.trim() || '',
                ar_orden: editForm.ar_orden
            };


            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/areas/editar-area/${area.ar_clave}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {

                await Swal.fire({
                    title: '¡Éxito!',
                    text: 'El área ha sido actualizada correctamente',
                    icon: 'success',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    }
                });

                onSuccess();
                onClose();
            } else {
                const errorData = await response.json();

                let errorTitle = 'Error al actualizar';
                let errorText = 'Ha ocurrido un error inesperado';
                let errorIcon = 'error';

                switch (response.status) {
                    case 400:
                        errorTitle = 'Error de validación';
                        if (errorData.error === 'ID de área inválido') {
                            errorText = 'El ID del área proporcionado no es válido. Por favor, intenta nuevamente.';
                        } else if (errorData.error === 'Datos insuficientes') {
                            errorText = 'Debe proporcionar al menos un campo para actualizar el área.';
                        } else if (errorData.error === 'Código inválido') {
                            errorText = 'El código del área no puede tener más de 2 caracteres.';
                            errorIcon = 'warning';
                        } else if (errorData.error === 'Código duplicado') {
                            errorText = `Ya existe otra área con el código "${editForm.ar_codigo}" en este test. Por favor, elige un código diferente.`;
                            errorIcon = 'warning';
                        } else if (errorData.error === 'Datos duplicados') {
                            errorText = 'Ya existe un área con ese código en este test. Por favor, utiliza un código único.';
                            errorIcon = 'warning';
                        } else if (errorData.error === 'Sin cambios') {
                            errorText = 'No se detectaron cambios válidos para actualizar en el área.';
                            errorIcon = 'info';
                        } else {
                            errorText = errorData.message || 'Los datos proporcionados no son válidos.';
                        }
                        break;

                    case 404:
                        errorTitle = 'Área no encontrada';
                        errorText = errorData.error === 'Área no encontrada'
                            ? `No se encontró el área con ID ${area.ar_clave}. Es posible que haya sido eliminada.`
                            : 'El área que intentas editar ya no existe en el sistema.';
                        errorIcon = 'warning';
                        break;

                    case 500:
                        errorTitle = 'Error del servidor';
                        if (errorData.error === 'Error interno del servidor') {
                            if (errorData.message === 'No se pudo verificar el área') {
                                errorText = 'No se pudo verificar la existencia del área. Por favor, intenta nuevamente.';
                            } else if (errorData.message === 'No se pudo verificar el código') {
                                errorText = 'No se pudo validar el código del área. Por favor, intenta nuevamente.';
                            } else if (errorData.message === 'No se pudo actualizar el área') {
                                errorText = 'Ocurrió un problema al guardar los cambios. Por favor, intenta nuevamente.';
                            } else {
                                errorText = 'Ocurrió un error interno en el servidor. Por favor, intenta nuevamente más tarde.';
                            }
                        } else {
                            errorText = errorData.message || 'Error interno del servidor. Por favor, contacta al administrador.';
                        }
                        break;

                    default:
                        errorText = errorData.message || `Error ${response.status}: ${response.statusText}`;
                        break;
                }

                Swal.fire({
                    title: errorTitle,
                    text: errorText,
                    icon: errorIcon,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57', 
                    customClass: { 
                        popup: 'popupbonito', 
                    },
                });
            }
        } catch (error) {
            console.error('Error al actualizar área:', error);

            let errorTitle = 'Error de conexión';
            let errorText = 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.';

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorText = 'No se pudo establecer conexión con el servidor. Verifica que el servidor esté funcionando.';
            } else if (error.name === 'SyntaxError') {
                errorTitle = 'Error de respuesta';
                errorText = 'El servidor envió una respuesta inválida. Por favor, intenta nuevamente.';
            } else if (error.message) {
                errorText = `Error inesperado: ${error.message}`;
            }

            Swal.fire({
                title: errorTitle,
                text: errorText,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57', 
                customClass: { 
                    popup: 'popupbonito', 
                }
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (saving) return;

        setEditForm({
            ar_codigo: '',
            ar_nombre: '',
            ar_descripcion: '',
            ar_orden: 0
        });
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !saving) {
            handleCancel();
        }
    };

    const handleModalClick = (e) => {
        e.stopPropagation();
    };

    if (!isOpen || !area) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div
                className={styles.modalContainer}
                onClick={handleModalClick}
                ref={modalRef}
            >
                <div className={styles.modalHeader}>
                    <h2>
                        Editar Área
                    </h2>
                    <button
                        className={styles.modalCloseBtn}
                        onClick={handleCancel}
                        disabled={saving}
                        type="button"
                        aria-label="Cerrar modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.modalContent}>
                    <div className={styles.preguntaForm}>
                        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                            <div className={styles.preguntaFormGroupWide}>
                                    <label htmlFor="codigo-input">
                                        Código 
                                    </label>
                                    <input
                                        id="codigo-input"
                                        type="text"
                                        className={styles.inputForm}
                                        value={editForm.ar_codigo}
                                        onChange={(e) => handleInputChange('ar_codigo', e.target.value.slice(0, 2))}
                                        maxLength="2"
                                        style={{ textTransform: 'uppercase' }}
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                            </div>
                            
                            <div className={styles.preguntaFormGroup}>
                                    <label htmlFor="orden-input">
                                        Orden
                                    </label>
                                    <input
                                        id="orden-input"
                                        type="number"
                                        className={styles.inputForm}
                                        value={editForm.ar_orden}
                                        onChange={(e) => handleInputChange('ar_orden', parseInt(e.target.value) || 0)}
                                        min="1"
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                            </div>

                            <div className={styles.preguntaFormGroup}>
                                    <label htmlFor="nombre-input">
                                        Nombre del Área 
                                    </label>
                                    <input
                                        id="nombre-input"
                                        type="text"
                                        className={styles.inputForm}
                                        value={editForm.ar_nombre}
                                        onChange={(e) => handleInputChange('ar_nombre', e.target.value)}
                                        placeholder="Nombre del área"
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                            </div>

                            <div className={styles.preguntaFormGroup}>
                                    <label htmlFor="descripcion-input">
                                        Descripción
                                    </label>
                                    <input
                                        id="descripcion-input"
                                        className={styles.inputForm}
                                        value={editForm.ar_descripcion}
                                        onChange={(e) => handleInputChange('ar_descripcion', e.target.value)}
                                        placeholder="Descripción del área"
                                        rows="2"
                                        disabled={saving}
                                    />
                            </div>
                        </form>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.preguntaFormActions}>
                        <button
                            className={styles.btnSave}
                            onClick={handleSave}
                            disabled={saving}
                            type="button"
                        >
                        {saving ? (
                                <>
                                <div className={styles.btnSpinner}></div>
                                Guardando...
                                </>
                            ) : (
                                <>
                                Editar Área
                                </>
                        )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditarResultados;